import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceItem } from '../../../types/invoiceTypes';
import { ProductData } from '../../../types/offerTypes';
import { IconMagic } from '../../../icons';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';
import UnitCodeSelector from '../../ui/UnitCodeSelector';
import {
  TAX_CATEGORY_KEYS,
  getTaxCategoryEntry,
  resolveExemptionReason,
  extractParagraphNr,
  requiresZeroRateCategoryChoice,
} from '../../../utils/taxCategoryCatalog';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS). Selektor nur sichtbar,
// wenn das PHP-Backend die Kategorie-Honorierung deployt hat (siehe Sequencing §10).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

// Tracking, welcher Preis zuletzt eingegeben wurde
interface PriceTracking {
  [itemId: string]: 'net' | 'gross';
}

interface MobileInvoiceItemRowProps {
  item: InvoiceItem;
  index: number;
  onItemChange: (id: string, field: keyof Omit<InvoiceItem, 'id'>, value: any) => void;
  onRemove: (id: string) => void;
  currency: string;
  productNameInput: string;
  setProductNameInput: (value: string) => void;
  showProductDropdown: boolean;
  setShowProductDropdown: (show: boolean) => void;
  onSelectProductSuggestion: (suggestion: ProductData) => void;
  userId: string | undefined;
  lastEditedPrice: 'net' | 'gross' | undefined;
  onEnhanceClick: (id: string) => void;
  isLoadingSuggestion: boolean;
  formatCurrency: (amount: number) => string;
  calculateItemNetAmount: (item: InvoiceItem) => number;
  calculateGrossPrice: (netPrice: number | undefined | null, taxRate: number) => number;
  calculateNetPrice: (grossPrice: number | undefined | null, taxRate: number) => number;
  productSuggestions: ProductData[];
  /** VB-1: Käufer-USt-IdNr. — für Inline-Hinweis bei K/AE-Positionen ohne USt-IdNr. */
  buyerVatId?: string | null;
}

// Autocomplete-Hook für Produkte (vereinfacht für mobile)
function useFirestoreAutocompleteProducts(searchValue: string, userId: string | undefined) {
  const [results, setResults] = useState<ProductData[]>([]);
  
  React.useEffect(() => {
    if (!userId || !searchValue || searchValue.length < 2) { 
      setResults([]); 
      return; 
    }
    
    // Vereinfachte Implementierung - in der Praxis würde hier die gleiche Logik wie in InvoiceItemsForm verwendet
    setResults([]);
  }, [userId, searchValue]);
  
  return results;
}

const sanitizeSignedDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^0-9,.\-]/g, '');
  const hasLeadingMinus = cleaned.startsWith('-');
  const withoutMinus = cleaned.replace(/-/g, '');
  const firstSeparatorIndex = withoutMinus.search(/[,.]/);
  let normalized = withoutMinus;

  if (firstSeparatorIndex !== -1) {
    const before = withoutMinus.slice(0, firstSeparatorIndex + 1);
    const after = withoutMinus.slice(firstSeparatorIndex + 1).replace(/[,.]/g, '');
    normalized = before + after;
    return `${hasLeadingMinus ? '-' : ''}${normalized}`;
  }

  return `${hasLeadingMinus ? '-' : ''}${normalized}`;
};

const MobileInvoiceItemRow: React.FC<MobileInvoiceItemRowProps> = ({
  item,
  index,
  onItemChange,
  onRemove,
  currency,
  productNameInput,
  setProductNameInput,
  showProductDropdown,
  setShowProductDropdown,
  onSelectProductSuggestion,
  userId,
  lastEditedPrice,
  onEnhanceClick,
  isLoadingSuggestion,
  formatCurrency,
  calculateItemNetAmount,
  calculateGrossPrice,
  calculateNetPrice,
  productSuggestions,
  buyerVatId,
}) => {
  const { t } = useTranslation('invoice');
  const [expanded, setExpanded] = useState(false);

  // --- VB-1: Steuerart (BT-151) ---
  const taxCategoryEntry = getTaxCategoryEntry(item.taxCategorySelection);
  const taxRateLocked = TAX_CATEGORY_ENABLED && !!taxCategoryEntry?.ratesLocked;
  // 0 %-Satz ohne eindeutige 0 %-Kategorie ⇒ prominente Pflicht-Auswahl inline.
  const needsZeroRateChoice =
    TAX_CATEGORY_ENABLED &&
    requiresZeroRateCategoryChoice(item.taxRate, item.taxCategorySelection);
  // Eine 0 %-gesperrte Kategorie (Z/E/AE/K/G) ist gewählt — Satz ist auf 0 fixiert.
  const isZeroCategorySelected = TAX_CATEGORY_ENABLED && !!taxCategoryEntry?.ratesLocked;
  // Inline-Panel nur im 0 %-Kontext; bei 19/7 unsichtbar (keine Reibung).
  const showTaxPanel = needsZeroRateChoice || isZeroCategorySelected;
  const taxPanelSelectValue = isZeroCategorySelected ? taxCategoryEntry!.selection : '';
  // Aufgelöster Pflicht-Hinweis (BT-121) bzw. optionaler §12(3)-BT-22-Hinweis.
  const mandatoryNote = taxCategoryEntry
    ? (item.exemptionReason || taxCategoryEntry.bt22HintText || null)
    : null;
  const buyerVatMissing =
    !!taxCategoryEntry?.requiresBuyerVatId && !(buyerVatId ?? '').trim();

  /**
   * Setzt eine neue Steuerart auf der Position: BT-151-Code, Befreiungsgrund und
   * (bei 0 %-Kategorien) den gesperrten USt-Satz. Bestehende §4-Nr. bleibt erhalten.
   * Wird `standard` aus dem 0 %-Kontext gewählt, wird der Satz wieder auf 19 %
   * freigegeben (verlässt die 0 %-Zone, blendet das Pflicht-Panel aus).
   */
  const handleTaxCategorySelect = (key: string) => {
    const entry = getTaxCategoryEntry(key);
    if (!entry) return;
    onItemChange(item.id, 'taxCategorySelection', entry.selection);
    onItemChange(item.id, 'taxCategory', entry.bt151Code);
    const existingNr = extractParagraphNr(entry.selection, item.exemptionReason);
    onItemChange(item.id, 'exemptionReason', resolveExemptionReason(entry.selection, existingNr));
    if (entry.ratesLocked) {
      onItemChange(item.id, 'taxRate', entry.allowedRates[0]);
    } else if (Number(item.taxRate) === 0) {
      onItemChange(item.id, 'taxRate', 19);
    }
  };

  /** Aktualisiert den §4-Befreiungstext anhand der eingegebenen konkreten Nr. */
  const handleParagraphNrChange = (nr: string) => {
    onItemChange(item.id, 'exemptionReason', resolveExemptionReason('exempt_4', nr));
  };
  const [editingFields, setEditingFields] = useState<{ [key: string]: boolean }>({});
  const [priceInputs, setPriceInputs] = useState<{ [key: string]: string }>({});
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [quantityInput, setQuantityInput] = useState('');

  // Calculate potentially precise display values
  const lastEditedType = lastEditedPrice; 
  const preciseDisplayGrossPrice = lastEditedType === 'gross' ? item.grossPrice : calculateGrossPrice(item.netPrice, item.taxRate);
  const preciseDisplayNetPrice = lastEditedType === 'net' ? item.netPrice : calculateNetPrice(item.grossPrice, item.taxRate);
  const lineItemNetAmount = calculateItemNetAmount(item);

  // Hilfsfunktion zur Formatierung von Preisen für die Anzeige
  const formatPriceForDisplay = (value: number | undefined | null, isEditing: boolean = false, fieldKey?: string): string => {
    if (isEditing && fieldKey && priceInputs[fieldKey] !== undefined) {
      return priceInputs[fieldKey];
    }
    
    if (value === null || value === undefined) return '';
    if (isEditing) {
      return value.toString();
    }
    return value.toFixed(2);
  };

  // Focus-Handler
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>, fieldKey?: string) => {
    event.target.select();
    
    if (fieldKey) {
      setEditingFields(prev => ({ ...prev, [fieldKey]: true }));
      
      if (fieldKey.includes('Price')) {
        const currentValue = fieldKey.includes('netPrice') ? item.netPrice : item.grossPrice;
        setPriceInputs(prev => ({ 
          ...prev, 
          [fieldKey]: currentValue?.toString() || '' 
        }));
      }
    }
  };

  // Blur-Handler für Preisformatierung
  const handlePriceBlur = (field: 'netPrice' | 'grossPrice', fieldKey: string) => {
    const stringValue = priceInputs[fieldKey] || '';
    
    if (stringValue.trim() === '' || stringValue.trim() === '-') {
      onItemChange(item.id, field, null);
    } else {
      const numericValue = parseFloat(stringValue.replace(',', '.'));
      if (!isNaN(numericValue)) {
        const formattedValue = Math.round(numericValue * 100) / 100;
        onItemChange(item.id, field, formattedValue);
      }
    }
    
    setEditingFields(prev => ({ ...prev, [fieldKey]: false }));
    setPriceInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[fieldKey];
      return newInputs;
    });
  };

  // Handler für Preisänderungen während der Eingabe
  const handlePriceChange = (fieldKey: string, value: string) => {
    const sanitizedValue = sanitizeSignedDecimalInput(value);
    setPriceInputs(prev => ({ ...prev, [fieldKey]: sanitizedValue }));
  };

  const handleQuantityFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(event);
    setIsEditingQuantity(true);
    setQuantityInput(item.quantity?.toString() || '');
  };

  const handleQuantityChange = (value: string) => {
    const sanitizedValue = sanitizeSignedDecimalInput(value);
    setQuantityInput(sanitizedValue);
    const numericValue = parseFloat(sanitizedValue.replace(',', '.'));
    if (!Number.isNaN(numericValue)) {
      onItemChange(item.id, 'quantity', numericValue);
    }
  };

  const handleQuantityBlur = () => {
    const stringValue = quantityInput.trim();
    if (stringValue === '' || stringValue === '-') {
      onItemChange(item.id, 'quantity', 0);
    } else {
      const numericValue = parseFloat(stringValue.replace(',', '.'));
      if (!Number.isNaN(numericValue)) {
        onItemChange(item.id, 'quantity', numericValue);
      }
    }
    setIsEditingQuantity(false);
    setQuantityInput('');
  };

  return (
    <div className="border border-strong rounded-lg bg-surface-inset mb-4 relative">
      {/* Header mit Position und Entfernen-Button */}
      <div className="flex items-center justify-between p-4 bg-surface-inset rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="bg-indigo-600 text-[#0C1322] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
            {index + 1}
          </div>
          <h4 className="text-primary font-medium">
            {item.name || t('positions.newPosition')}
          </h4>
        </div>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="text-muted hover:text-red-500 transition-colors p-2 -m-2"
          title={t('positions.removePosition')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Hauptinhalt */}
      <div className="p-4 space-y-4">
        {/* Produktname mit KI-Enhancement */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-secondary">
            {t('positions.name')} <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={productNameInput !== undefined ? productNameInput : item.name}
                onChange={e => { 
                  setProductNameInput(e.target.value); 
                  onItemChange(item.id, 'name', e.target.value);
                  setShowProductDropdown(true);
                }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                className="input-field placeholder-visible text-base py-3"
                placeholder={t('positions.namePlaceholder')}
                autoComplete="off"
                required
              />
              
              {/* Produktvorschläge Dropdown */}
              {showProductDropdown && productSuggestions && productSuggestions.length > 0 && (
                <div className="absolute z-10 bg-surface-hover border border-gray-500 rounded-md shadow-lg w-full mt-1 max-h-48 overflow-auto">
                  {productSuggestions.map(s => (
                    <div 
                      key={s.productId} 
                      className="px-4 py-3 hover:bg-gray-500 cursor-pointer border-b border-gray-500 last:border-b-0" 
                      onMouseDown={() => onSelectProductSuggestion(s)}
                    >
                      <div className="font-medium text-primary text-sm">{s.name}</div>
                      <div className="text-xs text-muted mt-1 flex justify-between">
                        <span>{s.description || ''}</span>
                        {s.taxRate !== undefined && <span className="font-semibold">{s.taxRate}% MwSt</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* KI-Enhancement Button */}
            <button
              type="button"
              onClick={() => onEnhanceClick(item.id)}
              className={`px-4 py-3 rounded-md border text-sm font-medium ${
                isLoadingSuggestion 
                  ? 'border-gray-500 bg-surface-hover text-muted cursor-not-allowed' 
                  : 'border-indigo-500 bg-surface-raised text-accent-ink hover:bg-surface-inset'
              }`}
              title="Name & Beschreibung verbessern (KI)"
              disabled={isLoadingSuggestion}
            >
              {isLoadingSuggestion ? (
                <svg className="animate-spin h-4 w-4 text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <IconMagic size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Beschreibung */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-secondary">
            {t('positions.description')}
          </label>
          <textarea
            rows={2}
            value={item.description || ''}
            onChange={e => onItemChange(item.id, 'description', e.target.value || null)}
            className="input-field placeholder-visible text-base py-3"
            placeholder={t('positions.descriptionPlaceholder')}
          />
        </div>

        {/* Hauptfelder: Menge, Preis, Steuer - Mobiloptimiert */}
        <div className="grid grid-cols-1 gap-4">
          {/* Menge */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
              {t('positions.quantity')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={isEditingQuantity ? quantityInput : item.quantity}
              onChange={e => handleQuantityChange(e.target.value)}
              required
              onFocus={handleQuantityFocus}
              onBlur={handleQuantityBlur}
              className="input-field placeholder-visible text-base py-3"
              placeholder="1"
            />
          </div>
          
          {/* Netto- und Bruttopreis nebeneinander */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
                {t('positions.unitPriceNet')}
              </label>
              <input
                type="text"
                value={formatPriceForDisplay(preciseDisplayNetPrice, editingFields[`netPrice-${item.id}`], `netPrice-${item.id}`)}
                onChange={e => handlePriceChange(`netPrice-${item.id}`, e.target.value)}
                onFocus={(e) => handleFocus(e, `netPrice-${item.id}`)}
                onBlur={(e) => handlePriceBlur('netPrice', `netPrice-${item.id}`)}
                className="input-field placeholder-visible text-base py-3"
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
                {t('positions.unitPriceGross')}
              </label>
              <input
                type="text"
                value={formatPriceForDisplay(preciseDisplayGrossPrice, editingFields[`grossPrice-${item.id}`], `grossPrice-${item.id}`)}
                onChange={e => handlePriceChange(`grossPrice-${item.id}`, e.target.value)}
                onFocus={(e) => handleFocus(e, `grossPrice-${item.id}`)}
                onBlur={(e) => handlePriceBlur('grossPrice', `grossPrice-${item.id}`)}
                className="input-field placeholder-visible text-base py-3"
                placeholder="0.00"
              />
            </div>
          </div>
          
          {/* Steuersatz und Gesamtbetrag */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
                {t('positions.vat')}
              </label>
              <input
                type="number"
                step="0.1"
                value={item.taxRate}
                onChange={e => onItemChange(item.id, 'taxRate', parseFloat(e.target.value) || 0)}
                onFocus={handleFocus}
                disabled={taxRateLocked}
                title={taxRateLocked ? t('taxCategory.rateLockedHint') : undefined}
                className={`input-field placeholder-visible text-base py-3 ${taxRateLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="19"
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-secondary min-h-[2.5rem] flex items-end pb-1">
                {t('positions.totalNet')}
              </label>
              <div className="block w-full px-4 py-3 border border-strong bg-[rgb(var(--surface-base-rgb)/0.5)] text-primary rounded-md text-base">
                {formatCurrency(lineItemNetAmount)}
              </div>
            </div>
          </div>
        </div>

        {/* VB-1: Inline Steuerart-Panel — erscheint NUR im 0 %-Kontext (sobald der
            Satz 0 ist bzw. eine 0 %-Kategorie gewählt wurde). Bei 19/7 unsichtbar
            (kein Selektor, keine Reibung). Offene Pflichtauswahl: amber + Platzhalter
            ohne Voreinstellung; nach eindeutiger Wahl ruhig indigo mit BT-121-Text. */}
        {showTaxPanel && (
          <div
            className={`mt-2 rounded-md px-3 py-3 ${
              needsZeroRateChoice
                ? 'border-2 border-amber-500/70 bg-[var(--tone-warning-bg)]'
                : 'border border-[var(--tone-accent-border)] bg-[var(--tone-accent-bg)]'
            }`}
          >
            {needsZeroRateChoice ? (
              <>
                <p className="text-sm font-semibold text-[var(--tone-warning-fg)]">
                  {t('taxCategory.zeroRateTitle')}
                </p>
                <p className="mt-1 text-xs text-[var(--tone-warning-fg)]">
                  {t('taxCategory.zeroRateHint')}
                </p>
              </>
            ) : (
              <p className="text-xs font-semibold text-accent-ink">
                {t('taxCategory.label')}
              </p>
            )}
            <div className="mt-2">
              <label htmlFor={`mobile-taxCategory-${item.id}`} className="sr-only">
                {t('taxCategory.label')}
              </label>
              <select
                id={`mobile-taxCategory-${item.id}`}
                value={taxPanelSelectValue}
                onChange={(e) => handleTaxCategorySelect(e.target.value)}
                className={`input-field text-base py-3 ${needsZeroRateChoice ? 'border-amber-500 text-[var(--tone-warning-fg)]' : ''}`}
              >
                <option value="" disabled>
                  {t('taxCategory.zeroRatePlaceholder')}
                </option>
                {TAX_CATEGORY_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`taxCategory.options.${key}`)}
                  </option>
                ))}
              </select>
            </div>
            {taxCategoryEntry?.requiresParagraphNr && (
              <div className="mt-2 space-y-1">
                <label
                  htmlFor={`mobile-paragraphNr-${item.id}`}
                  className="block text-xs text-secondary"
                >
                  {t('taxCategory.paragraphNrLabel')} <span className="text-[var(--tone-danger-fg)]">*</span>
                </label>
                <input
                  id={`mobile-paragraphNr-${item.id}`}
                  type="text"
                  value={extractParagraphNr(item.taxCategorySelection, item.exemptionReason)}
                  onChange={(e) => handleParagraphNrChange(e.target.value)}
                  className="input-field text-sm py-2"
                  placeholder={t('taxCategory.paragraphNrPlaceholder')}
                />
              </div>
            )}
            {!needsZeroRateChoice && mandatoryNote && (
              <p className="mt-2 text-sm text-[var(--tone-accent-fg)]">{mandatoryNote}</p>
            )}
            {buyerVatMissing && (
              <p className="mt-2 text-sm text-[var(--tone-danger-fg)]">{t('taxCategory.validation.buyerVatIdRequired')}</p>
            )}
            {!needsZeroRateChoice && (
              <p className="mt-2 text-xs text-muted">{t('taxCategory.responsibilityHint')}</p>
            )}
          </div>
        )}

        {/* Erweiterte Felder Toggle */}
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center space-x-2 text-accent-ink hover:text-accent-ink text-sm font-medium"
          >
            {expanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
            <span>{expanded ? t('positions.lessOptions') : t('positions.moreOptions')}</span>
          </button>
        </div>

        {/* Erweiterte Felder */}
        {expanded && (
          <div className="pt-4 border-t border-strong space-y-4">
            {/* VB-1: Der Steuerart-Selektor sitzt nicht mehr hier unter den
                Zusatzeinstellungen, sondern erscheint prominent inline am Posten,
                sobald der USt-Satz 0 ist (siehe Panel oben). */}
            {/* Einheit */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-secondary">
                {t('positions.unit')}
              </label>
              <UnitCodeSelector
                value={item.unitCode || null}
                onChange={(code) => onItemChange(item.id, 'unitCode', code)}
                placeholder={t('positions.unitPlaceholder') || 'Einheit wählen...'}
              />
            </div>
            
            {/* Artikelnummern */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary">
                  {t('positions.sellerItemRef')} (Opt.)
                </label>
                <input
                  type="text"
                  value={item.sellerArticleId || ''}
                  onChange={e => onItemChange(item.id, 'sellerArticleId', e.target.value || null)}
                  onFocus={handleFocus}
                  className="input-field placeholder-visible text-base py-3"
                  placeholder={t('positions.sellerItemRefPlaceholder')}
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary">
                  {t('positions.buyerItemRef')} (Opt.)
                </label>
                <input
                  type="text"
                  value={item.buyerArticleId || ''}
                  onChange={e => onItemChange(item.id, 'buyerArticleId', e.target.value || null)}
                  onFocus={handleFocus}
                  className="input-field placeholder-visible text-base py-3"
                  placeholder={t('positions.buyerItemRefPlaceholder')}
                />
              </div>
            </div>
            
            {/* Globale ID */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary">
                  {t('positions.globalId')} (Opt.)
                </label>
                <input
                  type="text"
                  value={item.globalId || ''}
                  onChange={e => onItemChange(item.id, 'globalId', e.target.value || null)}
                  onFocus={handleFocus}
                  className="input-field placeholder-visible text-base py-3"
                  placeholder={t('positions.globalIdPlaceholder')}
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary">
                  {t('positions.globalIdScheme')} (Opt.)
                </label>
                <input
                  type="text"
                  value={item.globalIdScheme || ''}
                  onChange={e => onItemChange(item.id, 'globalIdScheme', e.target.value || null)}
                  onFocus={handleFocus}
                  className="input-field placeholder-visible text-base py-3"
                  placeholder={t('positions.globalIdSchemePlaceholder')}
                />
              </div>
            </div>
            
            {/* Hilfetext */}
            <div className="text-xs text-muted">
              {t('positions.unitsHelp')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileInvoiceItemRow;
